/* eslint-disable react-refresh/only-export-components */
/**
 * ConfirmDialog + useConfirmDialog — FASE 2 / M1.
 *
 * Auditoria de Coerência (2026-05-21) identificou 11 usos de `confirm()`
 * nativo do browser — quebra design system (sem dark mode, branding, botão
 * destrutivo) + phrasing inconsistente.
 *
 * Padrão recomendado:
 *
 *   const confirm = useConfirmDialog();
 *
 *   async function handleDelete() {
 *     const ok = await confirm({
 *       title: 'Apagar?',
 *       message: 'Esta ação não pode ser desfeita.',
 *       variant: 'destructive',
 *     });
 *     if (ok) doDelete();
 *   }
 *
 *   // Render: <ConfirmDialog /> em algum lugar alto do tree (UMA vez).
 *   //   Idealmente no AppShell ou em volta do <Routes>.
 *
 * Alternativa (uso direto sem hook):
 *
 *   <ConfirmDialog
 *     open={open}
 *     title="..."
 *     message="..."
 *     onConfirm={() => ...}
 *     onCancel={() => setOpen(false)}
 *   />
 */

import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui';

export interface ConfirmOptions {
  title: string;
  message: string;
  /** Botão de confirmação. Default: 'Confirmar'. */
  confirmLabel?: string;
  /** Botão de cancelar. Default: 'Cancelar'. */
  cancelLabel?: string;
  /** 'destructive' usa botão vermelho. Default 'default'. */
  variant?: 'default' | 'destructive';
}

interface PendingConfirm extends ConfirmOptions {
  resolve: (ok: boolean) => void;
}

interface ConfirmContextValue {
  request: (opts: ConfirmOptions) => Promise<boolean>;
}

const ConfirmContext = createContext<ConfirmContextValue | null>(null);

/**
 * Hook pra disparar um confirm. Retorna função async que resolve true/false.
 * REQUER <ConfirmDialogProvider> no tree.
 */
export function useConfirmDialog(): (opts: ConfirmOptions) => Promise<boolean> {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    // Fallback gracioso: se ninguém montou o provider, cai pro confirm() nativo
    // (mantém apps existentes funcionando enquanto migração rola).
    return (opts: ConfirmOptions) =>
      Promise.resolve(
        typeof window !== 'undefined' && window.confirm(`${opts.title}\n\n${opts.message}`),
      );
  }
  return ctx.request;
}

interface ConfirmDialogProps {
  /** Quando integrado via useConfirmDialog hook, passa children = app tree. */
  children?: ReactNode;
}

/**
 * Provider + render do dialog. Monte UMA vez em volta do app (AppShell ou
 * <Routes>). Sem children, renderiza só o dialog sem provider — útil pra
 * compatibilidade controlled.
 */
export function ConfirmDialog({ children }: ConfirmDialogProps) {
  const [pending, setPending] = useState<PendingConfirm | null>(null);

  const request = useCallback((opts: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      setPending({ ...opts, resolve });
    });
  }, []);

  function handleConfirm() {
    pending?.resolve(true);
    setPending(null);
  }
  function handleCancel() {
    pending?.resolve(false);
    setPending(null);
  }

  const isOpen = pending !== null;
  const variant = pending?.variant ?? 'default';

  return (
    <ConfirmContext.Provider value={{ request }}>
      {children}
      <Dialog open={isOpen} onOpenChange={(o) => !o && handleCancel()} size="sm">
        <DialogHeader>
          <DialogTitle data-testid="confirm-dialog-title">{pending?.title ?? ''}</DialogTitle>
          {pending?.message && (
            <DialogDescription data-testid="confirm-dialog-message">
              {pending.message}
            </DialogDescription>
          )}
        </DialogHeader>
        <DialogContent>
          <div className="min-h-[8px]" aria-hidden />
        </DialogContent>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={handleCancel}
            data-testid="confirm-dialog-cancel"
          >
            {pending?.cancelLabel ?? 'Cancelar'}
          </Button>
          <Button
            variant={variant === 'destructive' ? 'destructive' : 'primary'}
            onClick={handleConfirm}
            data-testid="confirm-dialog-confirm"
          >
            {pending?.confirmLabel ?? 'Confirmar'}
          </Button>
        </DialogFooter>
      </Dialog>
    </ConfirmContext.Provider>
  );
}
